import storeManager from '../../store/StoreManager';
import cacheStatusActions, { cacheStatusTypes } from '../../actions/CacheStatusActions';
import apiService from '../../services/api/ApiService';
import candidateProgressConstants from '../../constants/CandidateProgressConstants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import ServiceWorkerService from '../../services/ServiceWorkerService';
import { cacheContexts } from './cacheManager';

const COMPUTE_TIMER = 3000;
const MONITOR_REPORT_TIMER = 20000;

let computeInterval;
let monitorReportInterval;
let previousProgressPerTest;

const previousMonitorProgress = {};

export const computeProgressPerTest = (progressByCache) => {
    const computedLoadProgress = {};
    Object.keys(progressByCache).forEach((identifier) => {
        const cacheProgress = progressByCache[identifier];
        let totalLoaded = 0;
        let combinedSize = 0;
        let lastUpdatedTime = 0;
        Object.keys(cacheProgress.files).forEach((url) => {
            const fileLoadData = cacheProgress.files[url];
            totalLoaded += fileLoadData.loaded;
            combinedSize += fileLoadData.total;
            lastUpdatedTime = Math.max(lastUpdatedTime, fileLoadData.timeStamp);
        });
        computedLoadProgress[identifier] = {
            totalLoaded,
            combinedSize,
            lastUpdatedTime,
            startTime: cacheProgress.startTime,
            hasFailed: cacheProgress.hasFailed,
        };
    });
    return computedLoadProgress;
};

const reportProgressToCacheState = (updatedProgressByCache) => {
    Object.keys(updatedProgressByCache).forEach((identifier) => {
        const computedLoadProgress = updatedProgressByCache[identifier];
        if (previousProgressPerTest?.[identifier]?.totalLoaded !== computedLoadProgress.totalLoaded) {
            storeManager.getStore().dispatch(
                cacheStatusActions.setLoadProgress(
                    identifier,
                    computedLoadProgress.totalLoaded,
                    computedLoadProgress.combinedSize,
                    computedLoadProgress.lastUpdatedTime,
                    computedLoadProgress.startTime,
                    computedLoadProgress.hasFailed,
                )
            );
        }
    });
    previousProgressPerTest = updatedProgressByCache;
};

const reportProgressOnTest = async (assessmentRunId, percentageLoaded) => {
    const previousProgress = previousMonitorProgress[assessmentRunId];
    if (previousProgress?.percentageLoaded !== percentageLoaded) {
        try {
            previousMonitorProgress[assessmentRunId] = { percentageLoaded };
            await apiService.notifyClientEvent(loadProgressHandler.progressEvent, percentageLoaded, true, parseInt(assessmentRunId, 10));
        } catch (e) {
            console.warn('Failed reporting progress on test', assessmentRunId, e);
        }
    }
};

const reportProgressToMonitor = () => {
    const { cacheStatus } = storeManager.getState();
    Object.keys(cacheStatus).filter((cacheKey) => {
        const cacheEntry = cacheStatus[cacheKey];
        return cacheEntry.status === cacheStatusTypes.IN_PROGRESS;
    }).forEach((cacheKey) => {
        const cacheEntry = cacheStatus[cacheKey];
        reportProgressOnTest(cacheKey, cacheEntry.loadProgress?.percentageLoaded || 0);
    });
};

const loadProgressHandler = {
    progressEvent: candidateProgressConstants.ASSETS_LOAD_PROGRESS,
    progressByCache: {},
    startComputingProgress() {
        computeInterval = setInterval(() => {
            try {
                reportProgressToCacheState(computeProgressPerTest(this.progressByCache));
            } catch (e) {
                console.error('Error computing progress', e);
            }
        }, COMPUTE_TIMER);
    },

    stopComputingProgress() {
        clearInterval(computeInterval);
    },

    addProgressData(logicalName, url, loaded, total, identifier, isDone = false, hasFailed = false) {
        ServiceWorkerService.clearReadableStreamValidation();
        const cacheEntry = this.progressByCache[identifier] || {};
        if (hasFailed) {
            cacheEntry.startTime = Date.now();
        } else if (!cacheEntry.startTime) {
            cacheEntry.startTime = Date.now();
            cacheEntry.files = {};
        }
        cacheEntry.hasFailed = hasFailed;
        cacheEntry.files[url] = {
            loaded,
            total,
            isDone,
            timeStamp: Date.now(),
        };
        this.progressByCache[identifier] = cacheEntry;
    },

    startReportingMonitorProgress() {
        monitorReportInterval = setInterval(() => {
            reportProgressToMonitor();
        }, MONITOR_REPORT_TIMER);
    },

    reportMonitorLoadStatusForOneTest(assessmentRunId, percentageLoaded) {
        reportProgressOnTest(assessmentRunId, percentageLoaded);
    },

    stopReportingMonitorProgress() {
        clearInterval(monitorReportInterval);
    },

    reportDownloadInfo(assessmentRunId, cacheContext, downloadSuccess, startedTries = 1, downloadTime = 0) {
        if (!marketplacePropertiesService.isRetrievingAssetsDownloadInfoEnabled() ||
            cacheContext !== cacheContexts.TEST) {
            return;
        }

        const extraData = {
            numberOfInterruptions: startedTries - 1,
            downloadSuccess,
        };

        if (downloadSuccess) {
            extraData.totalDownloadTime = downloadTime;
        }

        apiService.notifyClientEvent(candidateProgressConstants.ASSETS_DOWNLOAD_INFO, extraData, true, assessmentRunId);
    },
};

export default loadProgressHandler;
